import { cn } from '@/lib/utils';

interface RadioOption<T> {
	value: T;
	label: string;
}

interface RadioButtonGroupProps<T> {
	options: RadioOption<T>[];
	value: T;
	onChange: (value: T) => void;
	className?: string;
	name?: string;
}

interface RadioButtonProps<T> {
	option: RadioOption<T>;
	isSelected: boolean;
	onClick: () => void;
	name?: string;
}

const RadioButton = <T,>({
	option,
	isSelected,
	onClick,
	name,
}: RadioButtonProps<T>) => {
	return (
		<button
			name={name}
			type="button"
			onClick={onClick}
			className={cn(
				// Removido border-transparent e adicionado border-solid fixo
				'flex items-center justify-center whitespace-nowrap rounded-full border border-solid px-6 py-2 text-sm tracking-tight transition-colors focus:outline-none',
				isSelected
					? 'border-stone-600 bg-white font-bold text-stone-950 shadow-sm' // Azul mais forte (600) para a borda ser visível
					: 'border-stone-300 bg-white font-medium text-stone-500 hover:border-stone-400 hover:bg-stone-50', // Cinza mais forte (300/400)
			)}
			role="radio"
			aria-checked={isSelected}
			aria-label={option.label}
		>
			{option.label}
		</button>
	);
};

const RadioButtonGroup = <T,>({
	options,
	value,
	onChange,
	className = '',
	name,
}: RadioButtonGroupProps<T>) => {
	return (
		<div
			className={cn(
				'flex flex-wrap items-center justify-center gap-2 md:justify-start',
				className,
			)}
			role="radiogroup"
		>
			{options.map((option, index) => (
				<RadioButton
					key={index}
					option={option}
					isSelected={value === option.value}
					onClick={() => onChange(option.value)}
					name={name}
				/>
			))}
		</div>
	);
};

export { RadioButtonGroup };
export type { RadioOption, RadioButtonGroupProps };
